/** TRAILER BUILD PLANS™ — all customer-facing content and asset mapping. */

export interface PlanImage {
  src: string
  alt: string
  width: number
  height: number
}

export interface ProjectPage extends PlanImage {
  caption: string
}

export interface TechProof extends PlanImage {
  title: string
  project: string
  description: string
}

export interface Volume extends PlanImage {
  number: string
  title: string
  projects: string
  description: string
  /** Every project in the volume, as printed in the PDF index. */
  projectList: string[]
}

export interface Bonus extends PlanImage {
  number: string
  title: string
  description: string
}

export type SnapshotIcon = 'projects' | 'volumes' | 'dimensions' | 'bonus'

export interface SnapshotItem {
  label: string
  icon: SnapshotIcon
}

export interface Faq {
  question: string
  answer: string
}

/** Image host: light WebP files in the store's Impultienda resources. */
export const IMAGE_ORIGIN = 'https://aekpgsbqjzqntlbucbts.supabase.co'
const IMG = `${IMAGE_ORIGIN}/storage/v1/object/public/store-resources/7ab6f2d3-d4b5-47a8-abb9-74ecc3591b1c/`

export const PRODUCT = {
  name: 'TRAILER BUILD PLANS™',
  wordmarkA: 'Trailer',
  wordmarkB: 'Build Plans',
  descriptor: 'Trailer Plans to Build & Sell',
} as const

export const COMMERCIAL_RIGHTS = {
  label: 'COMMERCIAL BUILD RIGHTS INCLUDED',
  sentence: 'Build trailers for yourself — or build and sell the finished trailers to buyers near you.',
} as const

export const OFFER_ANCHOR = '#offer'
export const PREVIEW_ANCHOR = '#project-preview'

export const TICKER_ITEMS: string[] = [
  '30 TRAILER BUILDS',
  '6 DIGITAL VOLUMES',
  'DIMENSIONS & CUT LISTS',
  'BUILD & SELL CARDS',
  '5 FREE BONUS RESOURCES',
]

export const NAV_LINKS: Array<{ label: string; href: string }> = [
  { label: 'Preview', href: '#project-preview' },
  { label: 'Inside the Plans', href: '#technical-proof' },
  { label: 'Volumes', href: '#volumes' },
  { label: 'Bonuses', href: '#bonuses' },
  { label: 'FAQ', href: '#faq' },
]

/** All plan pages are vertical 4:5. */
const PAGE_SIZE = { width: 1100, height: 1376 }

const COVERS = {
  v1: `${IMG}1792821426643_tb-vol-1-c84634e0.webp`,
  v2: `${IMG}1792821432373_tb-vol-2-c0443fa2.webp`,
  v3: `${IMG}1792821438112_tb-vol-3-0940fd37.webp`,
  v4: `${IMG}1792821443537_tb-vol-4-e0f6061f.webp`,
  v5: `${IMG}1792821449078_tb-vol-5-a86ef734.webp`,
  v6: `${IMG}1792821358998_tb-vol-6-6bafc52c.webp`,
}

export const HERO_MOCKUP: PlanImage = {
  src: `${IMG}1792840955417_tb-hero-v2-7c6cd2bd.webp`,
  alt: 'TRAILER BUILD PLANS™ collection on the deck of a utility trailer: the six volumes, a tablet showing the P01 three-view plan, welding gloves and helmet, and a +5 free bonus guides badge',
  width: 1100,
  height: 1138,
}

export const SNAPSHOT: SnapshotItem[] = [
  { label: '30 Trailer Builds', icon: 'projects' },
  { label: '6 Organized Volumes', icon: 'volumes' },
  { label: 'Dimensions + Cut Lists', icon: 'dimensions' },
  { label: '5 Free Bonus Resources', icon: 'bonus' },
]

/** Project P01, Volume 01, PDF pages 06–11 — the complete project, in order. */
export const PREVIEW_PROJECT = {
  code: 'P01',
  name: '4 x 8 FT Utility Trailer',
  volume: 'Volume 01',
} as const

export const PROJECT_PAGES: ProjectPage[] = [
  {
    src: `${IMG}1792821461052_tb-p01-01-finished-52942dcd.webp`,
    caption: 'The finished build',
    alt: 'Volume 01, page 06: P01 4 x 8 FT Utility Trailer finished build — 48 x 96 in / 1219.2 x 2438.4 mm deck',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821466833_tb-p01-02-plan-f3309555.webp`,
    caption: '3-view dimensioned plan — inches & millimeters',
    alt: 'Volume 01, page 07: P01 front, side and top views with dimensions in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821471701_tb-p01-03-cutlist-7dbe2213.webp`,
    caption: 'Exploded view & complete cut list',
    alt: 'Volume 01, page 08: P01 exploded view and cut list in inches and millimeters',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821477009_tb-p01-04-steps-2febfc1b.webp`,
    caption: 'Step-by-step fabrication',
    alt: 'Volume 01, page 09: P01 illustrated fabrication sequence',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821482497_tb-p01-05-welds-81407da9.webp`,
    caption: 'Weld map & running-gear layout',
    alt: 'Volume 01, page 10: P01 weld map and running-gear layout',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821487693_tb-p01-06-sell-card-b6ad16e6.webp`,
    caption: 'Build & Sell card',
    alt: 'Volume 01, page 11: P01 Build & Sell card with finished size, materials and maker notes',
    ...PAGE_SIZE,
  },
]

export const PLAN_BENEFITS: string[] = ['Cut the steel.', 'Weld the frame square.', 'Tow it home — or sell it.']

export const TECH_PROOF: TechProof[] = [
  {
    src: `${IMG}1792821493354_tb-tech-01-p02-plan-c17190a0.webp`,
    title: 'KNOW THE DIMENSIONS',
    project: 'Project 02 · 5 x 8 FT Utility Trailer with Ramp Gate · Volume 01',
    description: 'Every view dimensioned before you cut a single part.',
    alt: 'Volume 01: P02 5 x 8 FT Utility Trailer with Ramp Gate three-view dimensioned plan',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821498877_tb-tech-02-p03-cutlist-a4f03cb7.webp`,
    title: 'KNOW WHAT TO CUT',
    project: 'Project 03 · 5 x 10 FT Utility Trailer with Side Rails · Volume 01',
    description: 'Parts, quantities, stock and exact cut sizes in one table.',
    alt: 'Volume 01: P03 5 x 10 FT Utility Trailer exploded view and complete cut list',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821504541_tb-tech-03-p04-welds-aff7d27c.webp`,
    title: 'KNOW WHERE TO WELD',
    project: 'Project 04 · 6 x 10 FT Mesh-Side Utility Trailer · Volume 01',
    description: 'Weld map and running-gear layout for every frame.',
    alt: 'Volume 01: P04 6 x 10 FT Mesh-Side Utility Trailer weld map and running-gear layout',
    ...PAGE_SIZE,
  },
  {
    src: `${IMG}1792821510220_tb-tech-04-p05-steps-1163a70e.webp`,
    title: 'FOLLOW THE BUILD',
    project: 'Project 05 · 6 x 12 FT Landscape Trailer · Volume 01',
    description: 'An illustrated sequence from cut parts to the finished trailer.',
    alt: 'Volume 01: P05 6 x 12 FT Landscape Trailer step-by-step fabrication',
    ...PAGE_SIZE,
  },
]

const COVER_SIZE = { width: 1100, height: 1376 }

export const VOLUMES: Volume[] = [
  {
    number: 'VOLUME 01',
    title: 'Utility Trailers',
    projects: '5 projects',
    description: 'The everyday trailers every town buys: 4 x 8 to 6 x 12 ft.',
    projectList: [
      'P01 — 4 x 8 FT Utility Trailer',
      'P02 — 5 x 8 FT Utility Trailer with Ramp Gate',
      'P03 — 5 x 10 FT Utility Trailer with Side Rails',
      'P04 — 6 x 10 FT Mesh-Side Utility Trailer',
      'P05 — 6 x 12 FT Landscape Trailer',
    ],
    src: COVERS.v1,
    alt: 'Volume 01 cover: Utility Trailers',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 02',
    title: 'Flatbed & Equipment Trailers',
    projects: '5 projects',
    description: 'Tandem flatbeds, a car hauler, an equipment trailer and more.',
    projectList: [
      'P06 — 7 x 16 FT Tandem Flatbed Trailer',
      'P07 — 7 x 18 FT Car Hauler with Slide-In Ramps',
      'P08 — 7 x 14 FT Equipment Trailer with Stand-Up Ramps',
      'P09 — 6 x 12 FT Dovetail Flatbed',
      'P10 — 5 x 10 FT Low-Deck Motorcycle & ATV Trailer',
    ],
    src: COVERS.v2,
    alt: 'Volume 02 cover: Flatbed & Equipment Trailers',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 03',
    title: 'ATV, Yard & Tow-Behind Trailers',
    projects: '5 projects',
    description: 'Off-road and yard trailers for ATVs and lawn tractors.',
    projectList: [
      'P11 — ATV Box Trailer (Off-Road Use)',
      'P12 — Lawn Tractor Dump Cart',
      'P13 — Utility Yard Wagon',
      'P14 — Firewood Trailer with Cradle Rack',
      'P15 — Garden Trailer with Removable Sides',
    ],
    src: COVERS.v3,
    alt: 'Volume 03 cover: ATV, Yard & Tow-Behind Trailers',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 04',
    title: 'Camping, Overland & Recreation',
    projects: '5 projects',
    description: 'Overland, teardrop, kayak, bike and rooftop-tent trailers.',
    projectList: [
      'P16 — Overland Utility Trailer with Tent Rack',
      'P17 — Teardrop Camper Chassis',
      'P18 — Kayak & Canoe Trailer',
      'P19 — Bike Rack Trailer (4 Bikes)',
      'P20 — Rooftop Tent Trailer Platform',
    ],
    src: COVERS.v4,
    alt: 'Volume 04 cover: Camping, Overland & Recreation Trailers',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 05',
    title: 'Work & Specialty Trailers',
    projects: '5 projects',
    description: 'Welding rig, landscaper, pipe, fence and smoker trailers.',
    projectList: [
      'P21 — Mobile Welding Rig Trailer',
      'P22 — Landscaper Mower Trailer with Tool Racks',
      'P23 — Pipe & Lumber Trailer',
      'P24 — Fence & Post Trailer',
      'P25 — BBQ Smoker Trailer Chassis',
    ],
    src: COVERS.v5,
    alt: 'Volume 05 cover: Work & Specialty Trailers',
    ...COVER_SIZE,
  },
  {
    number: 'VOLUME 06',
    title: 'Trailer Upgrades & Accessories',
    projects: '5 projects',
    description: 'Add-ons that upgrade any trailer — and sell on their own.',
    projectList: [
      'P26 — Tongue Tool Box',
      'P27 — Spare Tire Carrier',
      'P28 — Trailer Ramp Gate Kit',
      'P29 — Removable Side Rail Kit',
      'P30 — Manual Trailer Dolly',
    ],
    src: COVERS.v6,
    alt: 'Volume 06 cover: Trailer Upgrades & Accessories',
    ...COVER_SIZE,
  },
]

/** Bonus covers: the product mockups from the store's ebook design, as light WebP. */
const BONUS_SIZE = { width: 800, height: 800 }

export const BONUSES: Bonus[] = [
  {
    number: '01',
    title: 'Steel Purchase & Cut List Planner',
    description: 'Buy the right tube, angle and channel once, cut it in order and label every part.',
    src: `${IMG}1792840961368_tb-bonus-cover-01-613b547f.webp`,
    alt: 'Bonus 01 cover: Trailer Steel Purchase & Cut List Planner',
    ...BONUS_SIZE,
  },
  {
    number: '02',
    title: 'Running Gear & Axle Placement Worksheet',
    description: 'Place the axle and hangers right so the trailer tows straight.',
    src: `${IMG}1792840967054_tb-bonus-cover-02-b767668a.webp`,
    alt: 'Bonus 02 cover: Running Gear & Axle Placement Worksheet',
    ...BONUS_SIZE,
  },
  {
    number: '03',
    title: 'Trailer Wiring & Lighting Guide',
    description: 'Lights, plug and wire runs laid out step by step.',
    src: `${IMG}1792840972329_tb-bonus-cover-03-8780d6e2.webp`,
    alt: 'Bonus 03 cover: Trailer Wiring & Lighting Guide',
    ...BONUS_SIZE,
  },
  {
    number: '04',
    title: 'Costing & Pricing Workbook',
    description: 'Know what each trailer really costs before you set a price.',
    src: `${IMG}1792840977943_tb-bonus-cover-04-077acbe4.webp`,
    alt: 'Bonus 04 cover: Project Costing & Pricing Workbook (Trailer Edition)',
    ...BONUS_SIZE,
  },
  {
    number: '05',
    title: 'Local Trailer Selling Kit',
    description: 'Listing templates, photo setups and simple quotes for trailer buyers.',
    src: `${IMG}1792840983412_tb-bonus-cover-05-307e5798.webp`,
    alt: 'Bonus 05 cover: Local Trailer Selling Kit',
    ...BONUS_SIZE,
  },
]

export const OFFER_FEATURES: string[] = [
  '3-view dimensioned plans — every measurement in inches and millimeters',
  'Complete cut lists, exploded views and step-by-step fabrication',
  'Weld maps, running-gear layouts and a Build & Sell card for every project',
]

export const FAQS: Faq[] = [
  {
    question: 'What am I buying?',
    answer:
      'A digital collection of trailer build plans: 30 trailers and trailer accessories across six PDF volumes, plus five bonus resources.',
  },
  {
    question: 'Who is this collection for?',
    answer:
      'Welders and fabricators who already know how to weld and want ready-to-build trailer plans — for their own use or to build and sell.',
  },
  {
    question: 'Is this a beginner welding course?',
    answer:
      'No. The collection gives you the plans: dimensions, cut lists, drawings, build steps, weld maps and running-gear layouts. It does not teach welding from the beginning.',
  },
  {
    question: 'Do I have to build the axle and wheels?',
    answer:
      'No. Axles, springs, wheels, couplers, jacks, chains, fenders and lights are bought ready-made and bolted on. The plans show where they go; capacity always comes from the ratings on the parts you buy.',
  },
  {
    question: 'What kinds of trailers are included?',
    answer:
      'Utility trailers from 4 x 8 to 6 x 12 ft, tandem flatbeds, a car hauler, equipment and motorcycle trailers, ATV and yard trailers, camping and overland trailers, work trailers, and trailer upgrades like tool boxes, ramp gates and side rails.',
  },
  {
    question: 'Are the plans in inches or millimeters?',
    answer: 'Both. Every dimension on every project page is printed in inches and millimeters.',
  },
  {
    question: 'Are these physical books or trailers?',
    answer:
      'The product is digital. You receive PDF files by email right after payment; steel, running gear and tools are not included. Registration and road rules depend on where you live.',
  },
  {
    question: 'Can I preview the plans?',
    answer: 'Yes. All six pages of Project P01 — 4 x 8 FT Utility Trailer, from Volume 01, are in the preview section above.',
  },
  {
    question: 'Can I sell the trailers I build from these plans?',
    answer:
      'Yes. Your purchase includes permission to build projects from these plans for your own use or to sell the physical trailers and parts you make. You may print working copies for your own use. You may not share, resell, redistribute or sublicense the digital plans, PDFs, images or bonus files.',
  },
]

export const SUPPORT_EMAIL = 'atencion.creativodigital@gmail.com'

/** Single discreet guarantee line, shown only under the final purchase button. */
export const GUARANTEE_LINE = `7-day guarantee: if you have any problem with your purchase, email ${SUPPORT_EMAIL}.`
